import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, GraduationCap, ShieldCheck, Wand2, Lightbulb, Drama, Share2, Target,
  ShieldAlert, PhoneCall, HelpCircle, Wallet, HeartPulse, Flame, Trophy, ArrowRight, Plus, Check,
  ScanText, Rocket, Users, Utensils,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppMode } from "@/context/AppModeContext";
import AskPanel from "@/components/AskPanel";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function Tile({ icon: Icon, title, desc, to, big, testid }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={testid}
      className={`group text-left rounded-lg border border-border bg-card hover:border-primary hover:-translate-y-0.5 transition-all ${big ? "p-8" : "p-5"}`}
    >
      <div className={`grid place-items-center rounded-xl bg-primary/10 text-primary ${big ? "h-16 w-16" : "h-11 w-11"}`}>
        <Icon className={big ? "w-8 h-8" : "w-5 h-5"} />
      </div>
      <div className={`font-head font-bold mt-3 ${big ? "text-2xl" : "text-base"}`}>{title}</div>
      {desc && <div className={`text-muted-foreground ${big ? "text-lg mt-1" : "text-sm"}`}>{desc}</div>}
    </button>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { mode, isSenior, isChild } = useAppMode();
  const [progress, setProgress] = useState(null);
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/goals").then((r) => setGoals(r.data.items)).catch(() => {});
  };
  useEffect(load, []);

  const addGoal = async () => {
    if (!newGoal.trim()) return;
    const { data } = await api.post("/goals", { title: newGoal.trim() });
    setGoals((g) => [data, ...g]);
    setNewGoal("");
  };
  const toggleGoal = async (g) => {
    const { data } = await api.put(`/goals/${g.id}`);
    setGoals((gs) => gs.map((x) => (x.id === g.id ? { ...x, done: data.done } : x)));
    if (data.done) toast.success("+20 XP · goal complete");
    load();
  };

  // ---------- SENIOR ----------
  if (isSenior) {
    const cards = [
      { icon: Sparkles, title: "Ask a Question", desc: "Type or speak", to: "/ask", testid: "senior-ask" },
      { icon: ScanText, title: "Read This For Me", desc: "Explain a message or bill", to: "/read", testid: "senior-read" },
      { icon: Utensils, title: "Scan My Food", desc: "See what's in your meal", to: "/nutrition", testid: "senior-nutrition" },
      { icon: ShieldAlert, title: "Is This a Scam?", desc: "Check a message", to: "/safety", testid: "senior-scam" },
      { icon: GraduationCap, title: "Learn Something", desc: "Simple lessons", to: "/learn", testid: "senior-learn" },
      { icon: Users, title: "Contact Family", desc: "Share & get cheers", to: "/family", testid: "senior-family" },
      { icon: HelpCircle, title: "Help", desc: "How to use this", to: "/profile", testid: "senior-help" },
    ];
    return (
      <div className="space-y-8">
        <h1 className="font-head font-extrabold text-4xl">Hello, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-2xl text-muted-foreground -mt-4">What would you like to do today?</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {cards.map((c) => <Tile key={c.title} {...c} big />)}
        </div>
      </div>
    );
  }

  // ---------- CHILD ----------
  if (isChild) {
    return (
      <div className="space-y-7">
        <div>
          <h1 className="font-head font-extrabold text-4xl">Hi {user?.name?.split(" ")[0]}! 🌟</h1>
          <p className="text-xl text-muted-foreground mt-1">Let's learn how to handle real life — the fun way.</p>
        </div>
        <AskPanel compact />
        <h2 className="font-head font-bold text-2xl">Fun things to try</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tile icon={GraduationCap} title="Learn & Play" desc="Cool skills" to="/learn" testid="child-learn" />
          <Tile icon={Drama} title="Pretend & Practice" desc="Try real talks" to="/practice" testid="child-practice" />
          <Tile icon={ShieldCheck} title="Stay Safe" desc="Be smart online" to="/safety" testid="child-safety" />
          <Tile icon={ScanText} title="Read This" desc="What does it say?" to="/read" testid="child-read" />
          <Tile icon={Utensils} title="Scan Food" desc="What's on my plate?" to="/nutrition" testid="child-nutrition" />
          <Tile icon={Target} title="My Stars" desc="See progress" to="/skills" testid="child-progress" />
          <Tile icon={Share2} title="Explore" desc="How things connect" to="/multiverse" testid="child-multiverse" />
          <Tile icon={Lightbulb} title="Big Ideas" desc="Invent something" to="/ideas" testid="child-ideas" />
        </div>
      </div>
    );
  }

  // ---------- TEEN / STUDENT / ADULT / PROFESSIONAL ----------
  const level = progress?.level || 1;
  const xp = progress?.xp || 0;
  const pct = Math.min(100, Math.round(((xp % 200) / 200) * 100));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-muted-foreground font-head">{greeting()},</p>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl">{user?.name?.split(" ")[0]} <span className="text-muted-foreground text-lg font-medium capitalize">· {mode} mode</span></h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2"><Flame className="w-4 h-4 text-primary" /><span className="font-bold">{progress?.streak || 0}</span><span className="text-sm text-muted-foreground">streak</span></div>
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2"><Trophy className="w-4 h-4 text-primary" /><span className="font-bold">Lv {level}</span></div>
        </div>
      </div>

      {/* Ask hero */}
      <section>
        <h2 className="font-head font-bold text-xl mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Ask inizio anything</h2>
        <AskPanel compact />
      </section>

      {/* Bento */}
      <section className="grid md:grid-cols-12 gap-4">
        <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
          <Tile icon={Rocket} title="Career Test-Drive" desc="Actually try a job before choosing it" to="/career" testid="tile-career" />
          <Tile icon={Utensils} title="Scan Food 📸" desc="Estimate your meal's nutrition" to="/nutrition" testid="tile-nutrition" />
          <Tile icon={ScanText} title="Read This For Me" desc="Explain any message, form or bill" to="/read" testid="tile-read" />
          <Tile icon={Wand2} title="Life Tools" desc="Reality Check · Life Path · What Am I Missing" to="/tools" testid="tile-tools" />
          <Tile icon={Lightbulb} title="Idea Lab" desc="Unique ideas + brutal reality mode" to="/ideas" testid="tile-ideas" />
          <Tile icon={Drama} title="Practice & Simulate" desc="Interviews, negotiation, roleplay" to="/practice" testid="tile-practice" />
          <Tile icon={Users} title="Family & School" desc="Share progress · get cheers" to="/family" testid="tile-family" />
        </div>

        <div className="md:col-span-4 rounded-lg border border-border bg-card p-5 flex flex-col" data-testid="progress-card">
          <div className="flex items-center justify-between">
            <h3 className="font-head font-bold">Progress</h3>
            <button onClick={() => navigate("/skills")} className="text-sm text-primary hover:underline">View all</button>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Level {level}</span><span className="font-bold">{xp} XP</span></div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-center">
            <div className="rounded-md bg-secondary p-3"><div className="font-head font-extrabold text-2xl">{progress?.completed_skills?.length || 0}</div><div className="text-xs text-muted-foreground">skills</div></div>
            <div className="rounded-md bg-secondary p-3"><div className="font-head font-extrabold text-2xl">{progress?.goals_done || 0}</div><div className="text-xs text-muted-foreground">goals done</div></div>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="rounded-lg border border-border bg-card p-5 sm:p-6" data-testid="goals-section">
        <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-primary" />Today's goals</h3>
        <div className="flex gap-2 mb-4">
          <input value={newGoal} onChange={(e) => setNewGoal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGoal()} data-testid="goal-input" placeholder="Add a goal…" className="flex-1 rounded-md border border-input bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" />
          <button onClick={addGoal} data-testid="goal-add" className="rounded-md bg-primary text-primary-foreground px-4 font-bold hover:brightness-110"><Plus className="w-5 h-5" /></button>
        </div>
        {goals.length === 0 ? (
          <p className="text-muted-foreground text-sm">No goals yet. Add one, or generate a plan in Life Tools → Life Path.</p>
        ) : (
          <ul className="space-y-2">
            {goals.map((g) => (
              <li key={g.id} className="flex items-center gap-3">
                <button onClick={() => toggleGoal(g)} data-testid={`goal-toggle-${g.id}`} className={`h-6 w-6 rounded-md border-2 grid place-items-center transition-colors ${g.done ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                  {g.done && <Check className="w-4 h-4" />}
                </button>
                <span className={`flex-1 ${g.done ? "line-through text-muted-foreground" : ""}`}>{g.title}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
